import { invalidate } from '@react-three/fiber';
import { type BufferGeometry, Matrix4 } from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

import {
    SettingModelSettingType,
    SolvePanelPlacementModelGapPositionPreferences,
    type SolverCostWeights
} from 'api/models';
import { getInstanceLodLevel } from 'app/modules/wallforming/components/scenes/objects/articles/utils/articleLodUtils';
import {
    updateMultiPartInstanceEdges,
    updateSinglePartInstanceEdges
} from 'app/modules/wallforming/components/scenes/objects/articles/utils/edgesOverlayUtils';
import { POIFilterType } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiFilterType';
import { PoiNavigationSource } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiNavigationSource';
import { ShortcutSection } from 'app/modules/wallforming/components/scenes/toolbar/types/shortcutSection';
import { InteractionMode } from 'app/modules/wallforming/components/scenes/types/interactionMode';
import { PlacementTab } from 'app/modules/wallforming/components/setting-container/types/placementTab';
import { SettingView } from 'app/modules/wallforming/components/setting-container/types/settingView';

import type { UiSliceState } from 'app/modules/wallforming/components/state-management/placement/states/uiSliceState';
import type { PlacementState } from 'app/modules/wallforming/components/state-management/placement/types';
import type { StateCreator } from 'zustand';

const defaultSolverCostWeights: SolverCostWeights = {
    instanceCount: 100,
    verticalOverbuild: 10,
    gapWidth: 1,
    gapPositionDeviation: 1,
    fillerUsage: 25,
    fillerWidthPenalty: 5,
    materialScarcity: 0,
    groupPreferenceBonus: -10,
    lockedPanelChangePenalty: 1000,
    price: 0,
    co2: 0,
    ppm: 0
};

const defaultCornerSolverCostWeights: SolverCostWeights = {
    ...defaultSolverCostWeights,
    gapWidth: 0,
    gapPositionDeviation: 0,
    fillerUsage: 0,
    fillerWidthPenalty: 0,
    price: 20,
    co2: 20,
    ppm: 20
};

export const createUiSlice: StateCreator<PlacementState, [], [], UiSliceState> = (set, get) => ({
    showSceneStatusBarControls: true,
    setShowSceneStatusBarControls: (show) => set({ showSceneStatusBarControls: show }),

    showHeatmapOverlay: false,
    setShowHeatmapOverlay: (show) => set({ showHeatmapOverlay: show }),

    showHeatmapLabels: false,
    setShowHeatmapLabels: (show) => set({ showHeatmapLabels: show }),

    showHeatmapHover: false,
    setShowHeatmapHover: (show) => set({ showHeatmapHover: show }),

    heatmapMetric: 'overall',
    setHeatmapMetric: (metric) => set({ heatmapMetric: metric }),

    heatmapColorMap: 'classic',
    setHeatmapColorMap: (colorMap) => set({ heatmapColorMap: colorMap }),

    poiSelection: { current: { filterType: undefined, index: undefined, source: undefined } },
    setPoiSelection: async (filterType, index, source = PoiNavigationSource.PointerSelection) => {
        const commitPoiSelection = (clearConnectorSnapData = true) => {
            const state = get();

            if (filterType === POIFilterType.Corner) {
                requestAnimationFrame(() => {
                    state.setFaceSelectionRef(null);
                    invalidate();
                });
            }

            set({ poiSelection: { current: { filterType, index, source } } });
            if (clearConnectorSnapData) {
                state.clearConnectorSnapData();
            }
        };

        const state = get();
        if (state.arePivotControlsActive) {
            state.setArePivotControlsActive(false);
        }
        
        if (state.isAutoDragActiveRef.current) {
            const canRetarget = await state.updateAutoDragTarget(filterType, index);
            if (!canRetarget) {
                return;
            }

            commitPoiSelection(filterType !== POIFilterType.Corner);
            return;
        }

        commitPoiSelection();
    },

    workingPlane: null,
    setWorkingPlane: (plane) => set({ workingPlane: plane }),

    settingTypeFilter: undefined,
    setSettingTypeFilter: (settingType) => set({ settingTypeFilter: settingType }),

    selectedSettingView: SettingView.AUTOMATED_PLACEMENT,
    setSelectedSettingView: (settingView: SettingView) => set({ selectedSettingView: settingView }),

    selectedArticleId: null,
    setSelectedArticleId: (id) => {
        get().setInsertionPoint(0, 0, 0);
        set({
            selectedArticleId: id,
            connectorSnapData: {
                articleId: null,
                candidates: [],
                plane: null,
                poiIndex: undefined,
                isLoading: false,
                settingType: undefined
            }
        });
    },

    isShortcutsPanelOpen: false,
    setIsShortcutsPanelOpen: (show) => set({ isShortcutsPanelOpen: show }),

    selectedShortcutSection: ShortcutSection.CAMERA,
    setSelectedShortcutSection: (section) => set({ selectedShortcutSection: section }),

    waitForPlacementData: false,

    connectorSnapData: {
        articleId: null,
        candidates: [],
        plane: null,
        poiIndex: undefined,
        isLoading: false,
        settingType: undefined
    },
    setConnectorSnapData: (articleId, poiIndex, candidates, plane, settingType) =>
        set({
            connectorSnapData: { articleId, poiIndex, candidates, plane, isLoading: false, settingType }
        }),

    setConnectorSnapDataLoading: (articleId, isLoading) =>
        set({
            connectorSnapData: { ...get().connectorSnapData, articleId, isLoading }
        }),
    clearConnectorSnapData: () =>
        set({
            connectorSnapData: {
                articleId: null,
                candidates: [],
                plane: null,
                poiIndex: undefined,
                isLoading: false,
                settingType: undefined
            }
        }),

    showInsertPointSelection: true,
    setShowInsertPointSelection: (show) => set({ showInsertPointSelection: show }),

    isPPMFilterActive: true,
    setIsPPMFilterActive: (isActive) => set({ isPPMFilterActive: isActive }),

    graphicsSettings: {
        shadowsEnabled: false,
        reflectionsEnabled: false,
        aoQuality: 'off',
        multisampling: 0,
        edgesOverlayEnabled: true,
        edgeLineThickness: 'medium',
        materialQuality: 'basic',
        showIfc: false,
        showFps: false
    },
    setGraphicsSettings: (settings) =>
        set((state) => ({ graphicsSettings: { ...state.graphicsSettings, ...settings } })),

    hiddenPOIFilterTypes: [],
    resetHiddenPOIFilterTypes: () => set({ hiddenPOIFilterTypes: [] }),
    addHiddenPOIFilterTypes: (filterType) =>
        set((state) => ({
            hiddenPOIFilterTypes: state.hiddenPOIFilterTypes.includes(filterType)
                ? state.hiddenPOIFilterTypes
                : [...state.hiddenPOIFilterTypes, filterType]
        })),
    removeHiddenPOIFilterTypes: (filterType) =>
        set((state) => ({
            hiddenPOIFilterTypes: state.hiddenPOIFilterTypes.filter((type) => type !== filterType)
        })),

    isArticleInsertionEnabled: () => {
        const state = get();
        return state.selectedArticleId !== null && state.poiSelection.current.filterType === POIFilterType.Wall;
    },

    articleEdgesOverlay: null,
    setArticleEdgesOverlay: (articleEdgesOverlay) => set({ articleEdgesOverlay }),

    updateArticleEdgesOverlay: (instanceLodOverrides) => {
        const state = get();
        if (state.isDraggingActive() && !state.isDragNewArticleActiveRef.current) {
            return;
        }

        const edgesArray: BufferGeometry[] = [];
        const tmpMatrix = new Matrix4();
        const camera = state.mainCameraControlsRef.current?.camera;
        const hiddenInstanceIds = state.hiddenInstanceIds;

        const articles = state.articles;
        Object.values(articles).forEach((article) => {
            const edgeGeometries = article.edgeGeometries;
            if (!edgeGeometries) {
                return;
            }

            const partGeometries = Object.entries(edgeGeometries);
            if (partGeometries.length === 0) {
                return;
            }

            if (partGeometries.length === 1) {
                updateSinglePartInstanceEdges(
                    {
                        ...article,
                        articleInstances: Object.fromEntries(
                            Object.entries(article.articleInstances).filter(
                                ([instanceId]) => !hiddenInstanceIds.has(instanceId)
                            )
                        )
                    },
                    edgesArray,
                    tmpMatrix,
                    (instanceId, instance) => {
                        const overrideLodLevel = instanceLodOverrides?.get(instanceId);
                        if (overrideLodLevel !== undefined) {
                            return overrideLodLevel;
                        }
                        const lodPart = Object.values(instance.parts)[0];
                        return lodPart ? getInstanceLodLevel(camera, lodPart.position, false) : 0;
                    }
                );
            } else {
                Object.entries(article.articleInstances).forEach(([instanceId, instance]) => {
                    if (hiddenInstanceIds.has(instanceId)) {
                        return;
                    }
                    const overrideLodLevel = instanceLodOverrides?.get(instanceId);
                    const lodPart = Object.values(instance.parts)[0];
                    const lodLevel =
                        overrideLodLevel !== undefined
                            ? overrideLodLevel
                            : lodPart
                              ? getInstanceLodLevel(camera, lodPart.position, false)
                              : 0;
                    updateMultiPartInstanceEdges(instanceId, instance, edgeGeometries, edgesArray, tmpMatrix, lodLevel);
                });
            }
        });

        if (edgesArray.length > 0) {
            const merged = mergeGeometries(edgesArray, false);
            get().setArticleEdgesOverlay(merged);
        } else {
            get().setArticleEdgesOverlay(null);
        }

        invalidate();
    },

    isContextMenuVisible: false,
    setIsContextMenuVisible: (isVisible) => set({ isContextMenuVisible: isVisible }),

    cameraFrustumViewerRef: { current: null },
    setCameraFrustumViewerRef: (ref) => set({ cameraFrustumViewerRef: { current: ref } }),

    cameraHelperData: null,
    setCameraHelperData: (data) => set({ cameraHelperData: data }),

    solverDebugInfo: null,
    setSolverDebugInfo: (info) => set({ solverDebugInfo: info }),

    placementSolutions: [],
    setPlacementSolutions: (solutions) =>
        set((state) => {
            const isSelectionEmpty = state.selectedPlacementSolution === null && solutions.length > 0;
            const isSelectionOutOfBounds =
                state.selectedPlacementSolution !== null && state.selectedPlacementSolution >= solutions.length;
            if (isSelectionEmpty || isSelectionOutOfBounds) {
                return { placementSolutions: solutions, selectedPlacementSolution: 0 };
            }
            return { placementSolutions: solutions };
        }),

    selectedPlacementSolution: null,
    setSelectedPlacementSolution: (index) => set({ selectedPlacementSolution: index }),

    appliedCornerPlacementSolutionByPoi: {},
    setAppliedCornerPlacementSolutionForPoi: (poiIndex, solutionKey) =>
        set((state) => ({
            appliedCornerPlacementSolutionByPoi: {
                ...state.appliedCornerPlacementSolutionByPoi,
                [poiIndex]: solutionKey
            }
        })),

    showPlacementPreview: true,
    setShowPlacementPreview: (show) => set({ showPlacementPreview: show }),

    solverUseForcedGapMode: false,
    setSolverUseForcedGapMode: (useForcedGapMode) => set({ solverUseForcedGapMode: useForcedGapMode }),

    solverForcedGapMode: SolvePanelPlacementModelGapPositionPreferences.AfterFirst,
    setSolverForcedGapMode: (forcedGapMode) => set({ solverForcedGapMode: forcedGapMode }),

    solverRequireVerticalGap: false,
    setSolverRequireVerticalGap: (requireVerticalGap) => set({ solverRequireVerticalGap: requireVerticalGap }),

    solverCostWeights: defaultSolverCostWeights,
    setSolverCostWeights: (weights) =>
        set((state) => ({
            solverCostWeights: {
                ...state.solverCostWeights,
                ...weights
            }
        })),

    automatedPlacementTab: PlacementTab.CATALOGUE,
    setAutomatedPlacementTab: (tab) => set({ automatedPlacementTab: tab }),

    selectedSettingType: SettingModelSettingType.Panels,
    setSelectedSettingType: (settingType) => set({ selectedSettingType: settingType }),

    automatedPlacementSettingsByType: {
        Panels: {
            requireVerticalGap: false,
            gapPositionPreferences: SolvePanelPlacementModelGapPositionPreferences.AfterFirst,
            solverCostWeights: defaultSolverCostWeights
        },
        Connectors: {
            solverCostWeights: defaultSolverCostWeights
        },
        Corners: {
            solverCostWeights: defaultCornerSolverCostWeights
        },
        StopEnd: {
            solverCostWeights: defaultSolverCostWeights
        }
    },
    setAutomatedPanelRequireVerticalGap: (requireVerticalGap) =>
        set((state) => ({
            automatedPlacementSettingsByType: {
                ...state.automatedPlacementSettingsByType,
                Panels: {
                    ...state.automatedPlacementSettingsByType.Panels,
                    requireVerticalGap
                }
            }
        })),
    setAutomatedPanelGapPositionPreferences: (mode) =>
        set((state) => ({
            automatedPlacementSettingsByType: {
                ...state.automatedPlacementSettingsByType,
                Panels: {
                    ...state.automatedPlacementSettingsByType.Panels,
                    gapPositionPreferences: mode
                }
            }
        })),
    setAutomatedSolverCostWeightsByType: (settingType, weights) =>
        set((state) => {
            const current = state.automatedPlacementSettingsByType;

            if (settingType === SettingModelSettingType.Panels) {
                return {
                    automatedPlacementSettingsByType: {
                        ...current,
                        Panels: {
                            ...current.Panels,
                            solverCostWeights: {
                                ...current.Panels.solverCostWeights,
                                ...weights
                            }
                        }
                    }
                };
            }

            if (settingType === SettingModelSettingType.Corners) {
                return {
                    automatedPlacementSettingsByType: {
                        ...current,
                        Corners: {
                            ...current.Corners,
                            solverCostWeights: {
                                ...current.Corners.solverCostWeights,
                                ...weights
                            }
                        }
                    }
                };
            }

            if (settingType === SettingModelSettingType.Connectors) {
                return {
                    automatedPlacementSettingsByType: {
                        ...current,
                        Connectors: {
                            ...current.Connectors,
                            solverCostWeights: {
                                ...current.Connectors.solverCostWeights,
                                ...weights
                            }
                        }
                    }
                };
            }

            return {
                automatedPlacementSettingsByType: {
                    ...current,
                    StopEnd: {
                        ...current.StopEnd,
                        solverCostWeights: {
                            ...current.StopEnd.solverCostWeights,
                            ...weights
                        }
                    }
                }
            };
        }),

    disableCameraAlignBySource: {
        [PoiNavigationSource.PointerSelection]: true,
        [PoiNavigationSource.PoiStepper]: true
    },
    setDisableCameraAlign: (source, disable) => {
        if (get().disableCameraAlignBySource[source] !== disable) {
            set((state) => ({
                disableCameraAlignBySource: { ...state.disableCameraAlignBySource, [source]: disable }
            }));
        }
    },

    interactionMode: InteractionMode.POI_NAVIGATION,
    setInteractionMode: (mode) => set({ interactionMode: mode }),

    isSpaceKeyPressed: false,
    setIsSpaceKeyPressed: (isPressed) => set({ isSpaceKeyPressed: isPressed })
});
